import { inject, Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormArray,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  IFooterBlockForm,
  IFooterForm,
  IFooterFormData,
  IFooterLayoutForm,
  IFooterPaymentProviderForm,
} from '../../entities/footer-entity';
import { Factory } from '../factory';
import { ImageFormsFactoryService } from '../image/image-forms-factory.service';
import { LinkFormsFactoryService } from '../link/link-forms-factory.service';
import { ILogoAreaForm } from '../../entities/logo-area-entity';
import { IAddressAreaForm } from '../../entities/address-area-entity';
import { IPaymentAreaForm } from '../../entities/payment-area-entity';
import { ILinkAreaForm } from '../../entities/link-area-entity';
import { FilterTextsPipe } from '../../pipes/filterTexts/filterTexts.pipe';
import { FOOTER_LAYOUT_ROW_BREAK } from '../../components/data-input/drag-drop-grid/drag-drop-grid';
import {
  DEFAULT_FOOTER,
  DEFAULT_FOOTER_BLOCKS,
  DEFAULT_PAYMENT_PROVIDERS,
  EFooterBlockType,
  DEFAULT_FOOTER_AREA_TYPE,
  IAddressArea,
  IFooter,
  IText,
  IFooterBlock,
  IFooterPaymentProvider,
  ERelationType,
  toFooterBlockType,
} from '@web/dtos';

@Injectable({
  providedIn: 'root',
})
export class FooterFormsFactoryService extends Factory<IFooterForm, IFooter> {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly imageFormsFactory: ImageFormsFactoryService = inject(
    ImageFormsFactoryService
  );
  private readonly linkFormsFactory: LinkFormsFactoryService = inject(
    LinkFormsFactoryService
  );
  private readonly filterTexts: FilterTextsPipe = inject(FilterTextsPipe);

  buildForm(entity: IFooter = DEFAULT_FOOTER): FormGroup<IFooterForm> {
    const savedBlocks = new Map(
      (entity.blocks ?? [])
        .filter(
          block =>
            block.positionOrder >= 1 &&
            block.positionOrder <= DEFAULT_FOOTER_BLOCKS.length &&
            block.id &&
            (block.positionOrder > 3 ||
              block.id === DEFAULT_FOOTER_BLOCKS[block.positionOrder - 1].id)
        )
        .map(block => [block.positionOrder, block])
    );
    const blocks = DEFAULT_FOOTER_BLOCKS.map((defaultBlock, index) => ({
      ...(savedBlocks.get(index + 1) ?? defaultBlock),
      positionOrder: index + 1,
    }));
    const defaultBlockOrder = DEFAULT_FOOTER.layout!.blockOrder;
    const legacyBlockIds: Record<string, string> = {
      logoArea: blocks[0].id as string,
      addressArea: blocks[1].id as string,
      paymentArea: blocks[2].id as string,
    };
    const customBlockIds = blocks
      .map(block => block.id)
      .filter((blockId): blockId is NonNullable<typeof blockId> => !!blockId);
    const validBlockIds = new Set([
      FOOTER_LAYOUT_ROW_BREAK,
      ...customBlockIds,
      'linkArea',
    ]);
    const blockOrder = [
      ...new Set(
        (entity.layout?.blockOrder ?? defaultBlockOrder)
          .map(blockId => legacyBlockIds[blockId] ?? blockId)
          .filter(blockId => validBlockIds.has(blockId))
      ),
      ...customBlockIds.filter(
        blockId =>
          !(entity.layout?.blockOrder ?? defaultBlockOrder)
            .map(id => legacyBlockIds[id] ?? id)
            .includes(blockId)
      ),
    ];
    const hiddenBlocks = [
      ...(entity.layout?.hiddenBlocks ?? [])
        .map(id => legacyBlockIds[id] ?? id)
        .filter(id => id !== 'linkArea' && validBlockIds.has(id)),
      ...blocks
        .filter(block => !savedBlocks.has(block.positionOrder))
        .map(block => block.id)
        .filter((blockId): blockId is NonNullable<typeof blockId> => !!blockId),
    ];

    return this.formBuilder.group<IFooterForm>({
      id: this.formBuilder.control(entity.id || null),

      backgroundColor: this.formBuilder.nonNullable.control(
        entity.backgroundColor,
        [Validators.required]
      ),

      appearance: this.formBuilder.group({
        showBackgroundImage: this.formBuilder.nonNullable.control(
          entity.appearance?.showBackgroundImage ?? false
        ),
        backgroundImage: this.formBuilder.control(
          entity.appearance?.backgroundImage ?? null
        ),
        backgroundImageAlt: this.formBuilder.control(
          entity.appearance?.backgroundImageAlt ?? null
        ),
        fontColor: this.formBuilder.nonNullable.control(
          entity.appearance?.fontColor ?? '#000000'
        ),
        backgroundColor: this.formBuilder.nonNullable.control(
          entity.appearance?.backgroundColor ?? '#FFFFFF'
        ),
        fontColorDark: this.formBuilder.nonNullable.control(
          entity.appearance?.fontColorDark ?? '#FFFFFF'
        ),
        backgroundColorDark: this.formBuilder.nonNullable.control(
          entity.appearance?.backgroundColorDark ?? '#121212'
        ),
      }),

      layout: this.formBuilder.group<IFooterLayoutForm>({
        blockOrder: new FormArray<FormControl<string>>(
          blockOrder.map(blockId =>
            this.formBuilder.nonNullable.control(blockId)
          )
        ),
        hiddenBlocks: new FormArray<FormControl<string>>(
          hiddenBlocks.map(blockId =>
            this.formBuilder.nonNullable.control(blockId)
          )
        ),
      }),
      blocks: this.formBuilder.array(
        blocks.map((block, index) =>
          this.buildBlockForm(
            block,
            index,
            entity.layout?.paymentProviders?.[block.id ?? '']
          )
        )
      ),
      logoArea: this.formBuilder.group<ILogoAreaForm>({
        id: this.formBuilder.control(entity.logoArea.id || null),
        isActive: this.formBuilder.nonNullable.control(
          entity.logoArea.isActive,
          [Validators.required]
        ),
        headlines: this.formBuilder.nonNullable.control(
          entity.logoArea.headlines
        ),
        accessibleImages:
          entity.logoArea.accessibleImages.length > 0
            ? this.imageFormsFactory.buildFormArray(
                entity.logoArea.accessibleImages
              )
            : this.formBuilder.array([
                this.imageFormsFactory.buildImageFormGroup(undefined, 0),
              ]),
        type: this.formBuilder.nonNullable.control(
          toFooterBlockType(
            entity.logoArea.type,
            DEFAULT_FOOTER_AREA_TYPE.logoArea
          )
        ),
        orientation: this.formBuilder.nonNullable.control(
          (entity.logoArea as any)?.orientation ?? 'vertical'
        ),
      }),
      addressArea: this.formBuilder.group<IAddressAreaForm>({
        id: this.formBuilder.control(entity.addressArea.id || null),
        isActive: this.formBuilder.nonNullable.control(
          entity.addressArea.isActive,
          [Validators.required]
        ),
        headlines: this.formBuilder.nonNullable.control(
          this.filterTexts.transform(
            entity.addressArea.texts,
            ERelationType.headline
          )
        ),
        texts: this.formBuilder.nonNullable.control(
          this.filterTexts.transform(
            entity.addressArea.texts,
            ERelationType.text
          )
        ),
        type: this.formBuilder.nonNullable.control(
          toFooterBlockType(
            entity.addressArea.type,
            DEFAULT_FOOTER_AREA_TYPE.addressArea
          )
        ),
      }),
      paymentArea: this.formBuilder.group<IPaymentAreaForm>({
        id: this.formBuilder.control(entity.paymentArea.id || null),
        isActive: this.formBuilder.nonNullable.control(
          entity.paymentArea.isActive,
          [Validators.required]
        ),
        headlines: this.formBuilder.nonNullable.control(
          entity.paymentArea.headlines
        ),
        accessibleImages: this.imageFormsFactory.buildFormArray(
          entity.paymentArea.accessibleImages
        ),
        type: this.formBuilder.nonNullable.control(
          toFooterBlockType(
            entity.paymentArea.type,
            DEFAULT_FOOTER_AREA_TYPE.paymentArea
          )
        ),
      }),
      linkArea: this.formBuilder.group<ILinkAreaForm>({
        id: this.formBuilder.control(entity.linkArea.id || null),
        headlines: this.formBuilder.nonNullable.control(
          entity.linkArea.headlines
        ),
        links: this.linkFormsFactory.buildFormArray(entity.linkArea.links),
        type: this.formBuilder.nonNullable.control(
          toFooterBlockType(
            entity.linkArea.type,
            DEFAULT_FOOTER_AREA_TYPE.linkArea
          )
        ),
      }),
    });
  }

  buildEntity(form: FormGroup<IFooterForm>): IFooter {
    const footer = form.getRawValue() as unknown as IFooterFormData;
    const blocks = form.controls.blocks.getRawValue();

    const newAddressArea: IAddressArea = {
      id: footer.addressArea.id,
      isActive: footer.addressArea.isActive,
      texts: [
        ...(footer.addressArea.texts ?? []),
        ...(footer.addressArea.headlines ?? []),
      ],
    };

    const newFooter: any = {
      id: footer.id,
      backgroundColor: footer.backgroundColor,
      appearance: footer.appearance,
      layout: {
        blockOrder: form.controls.layout.controls.blockOrder.getRawValue(),
        hiddenBlocks: form.controls.layout.controls.hiddenBlocks.getRawValue(),
        paymentProviders: Object.fromEntries(
          form.controls.blocks.controls
            .filter(
              block =>
                block.controls.id.value &&
                block.controls.type.value === EFooterBlockType.Payments
            )
            .map(block => [
              block.controls.id.value as string,
              block.controls.paymentProviders.getRawValue(),
            ])
        ),
      },
      logoArea: {
        ...footer.logoArea,
      },
      paymentArea: {
        ...footer.paymentArea,
      },
      linkArea: {
        ...footer.linkArea,
        isActive: true,
      },
      addressArea: newAddressArea,
      blocks: blocks.map((block, index) => {
        const { paymentProviders: _paymentProviders, ...savedBlock } = block;
        return {
          ...savedBlock,
          positionOrder: index + 1,
          links: block.links.map(link => ({
            ...link,
            texts: [...(link.texts ?? []), ...(link.urls ?? [])],
          })),
        };
      }),
    };

    newFooter.linkArea?.links?.forEach((group: any) => {
      const urls: IText[] = group.urls ?? [];
      const texts: IText[] = group.texts ?? [];
      group.texts = [...texts, ...urls];
    });

    this.removeNullIds(newFooter);
    return newFooter as IFooter;
  }

  buildBlockForm(
    block: IFooterBlock,
    positionOrder: number,
    configuredProviders: IFooterPaymentProvider[] = []
  ): FormGroup<IFooterBlockForm> {
    const providerIds = new Set<string>(
      DEFAULT_PAYMENT_PROVIDERS.map(provider => provider.id!)
    );
    const paymentProviders = [
      ...configuredProviders.filter(provider =>
        providerIds.has(provider.providerId)
      ),
      ...DEFAULT_PAYMENT_PROVIDERS.filter(
        provider =>
          !configuredProviders.some(
            configured => configured.providerId === provider.id
          )
      ).map(provider => ({
        providerId: provider.id!,
        visible: false,
        description: [],
        url: '',
      })),
    ];
    return this.formBuilder.group<IFooterBlockForm>({
      id: this.formBuilder.control(block.id ?? null),
      type: this.formBuilder.nonNullable.control(block.type),
      orientation: this.formBuilder.nonNullable.control(
        block.orientation ?? 'vertical'
      ),
      isActive: this.formBuilder.nonNullable.control(block.isActive),
      positionOrder: this.formBuilder.nonNullable.control(
        block.positionOrder ?? positionOrder
      ),
      headlines: this.formBuilder.nonNullable.control(block.headlines ?? []),
      texts: this.formBuilder.nonNullable.control(block.texts ?? []),
      accessibleImages:
        (block.accessibleImages?.length ?? 0) > 0
          ? this.imageFormsFactory.buildFormArray(block.accessibleImages)
          : block.type === EFooterBlockType.Default ||
              block.type === EFooterBlockType.Payments
            ? this.imageFormsFactory.buildFormArray([])
            : this.formBuilder.array([
                this.imageFormsFactory.buildImageFormGroup(undefined, 0),
              ]),
      links: this.linkFormsFactory.buildFormArray(block.links ?? []),
      paymentProviders: this.formBuilder.array<
        FormGroup<IFooterPaymentProviderForm>
      >(
        paymentProviders.map(provider =>
          this.formBuilder.group<IFooterPaymentProviderForm>({
            providerId: this.formBuilder.nonNullable.control(
              provider.providerId
            ),
            visible: this.formBuilder.nonNullable.control(provider.visible),
            description: this.formBuilder.nonNullable.control(
              provider.description
            ),
            url: this.formBuilder.nonNullable.control(provider.url),
          })
        )
      ),
    });
  }
}
